import React from 'react';
import { InsuranceDTO, InsuranceType } from '../../api/useInsuranceQuery';
import { InsuranceTable } from '../../components/InsuranceTable/InsuranceTable';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { ColumnHeader } from '../../../OtherAssets/OtherAssets.meta';
import { InsuranceToDelete, InsuranceToUpsert } from '../../Insurance.meta';
import { TableActions } from '../../components/TableActions/TableActions';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { useIsPdf } from '../../../../FinancialPlanOPL/hooks/useIsPdf';

const columnHeaders: ColumnHeader[] = [
  { text: 'Name' },
  { text: 'Owner' },
  { text: 'Type' },
  { text: 'Monthly Benefit', align: 'right' },
  { text: 'Elimination Period' },
  { text: 'Premium', align: 'right' },
];

interface Props {
  data: InsuranceDTO;
  setInsuranceToUpsert(insurance: InsuranceToUpsert): void;
  setInsuranceToDelete(insurance: InsuranceToDelete): void;
  workflowProps?: WorkflowComponentProps;
}

export const DisabilityInsurance = ({
  data,
  setInsuranceToUpsert,
  setInsuranceToDelete,
  workflowProps,
}: Props) => {
  const items = data.Items.Disability || [];

  const { DisabilityInsurancePolicyTypeOptions } = data.Options;

  const isPrint = useIsPdf();

  return (
    <InsuranceTable
      title="Disability Insurance Policies"
      isEmpty={items.length === 0}
      columnHeaders={columnHeaders}
      onAddInsurance={() => setInsuranceToUpsert({ InsuranceType: InsuranceType.Disability })}
      workflowProps={workflowProps}
    >
      {items.map(insurance => (
        <tr key={insurance.AccountId}>
          <td>
            <Text fontWeight={500} textAlign="left" width="100%">
              {insurance.Description}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {insurance.AccountOwner}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {
                DisabilityInsurancePolicyTypeOptions.find(
                  ({ value }) => value === insurance.DisabilityInsurancePolicyType,
                )?.label
              }
            </Text>
          </td>

          <td>
            <Text variant="tabular" fontWeight={500} textAlign="right">
              {insurance.DisabilityMonthlyBenefitAmount !== null
                ? formatCurrency(insurance.DisabilityMonthlyBenefitAmount)
                : ''}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {insurance.DisabilityEliminationPeriod
                ? insurance.DisabilityEliminationPeriod.DisplayString
                : ''}
            </Text>
          </td>

          <td>
            <Text variant="tabular" fontWeight={500} textAlign="right">
              {insurance.Premium !== null ? formatCurrency(insurance.Premium) : ''}
            </Text>
          </td>

          {!isPrint && (
            <TableActions
              insuranceName={insurance.Description}
              onEdit={() => setInsuranceToUpsert(insurance)}
              onDelete={() => setInsuranceToDelete({ id: insurance.AccountId })}
            />
          )}
        </tr>
      ))}
    </InsuranceTable>
  );
};
